import axios from "axios";
import { useState, useRef, useEffect } from "react";
import { API_URL } from "../../../env";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { PERMISSIONS, hasRoleAccess } from "../../lib/rbac";
import { useAuth } from "../../context/AuthContext";

const MANAGE_LINKS = [
  {
    label: "Add Employee",
    href: "/admin/add/user",
    roles: PERMISSIONS.USER_ADD,
  },
  {
    label: "Add Company",
    href: "/admin/add/company",
    roles: PERMISSIONS.MASTER_DATA_MANAGE,
  },
  {
    label: "Add Product",
    href: "/admin/add/product",
    roles: PERMISSIONS.MASTER_DATA_MANAGE,
  },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const dropRef = useRef(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const visibleManageLinks = MANAGE_LINKS.filter((link) =>
    hasRoleAccess(user?.role, link.roles),
  );

  useEffect(() => {
    const handler = (e) => {
      if (e.altKey && e.key.toLowerCase() === "h") {
        e.preventDefault();
        navigate("/admin/home", { replace: false });
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [navigate]);

  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  async function logout() {
    try {
      const res = await axios.post(
        `${API_URL}/emp/logout`,
        {},
        { withCredentials: true },
      );
      toast.success(res.data.message);
      setTimeout(() => navigate("/", { replace: true }), 500);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Something went wrong.");
    }
  }

  return (
    <nav className="sticky top-0 z-50 border-b border-gray-100 bg-white print:hidden">
      <div className="mx-auto flex h-14 max-w-5xl items-center justify-between px-4 md:px-8">
        {/* Logo */}
        <a href="/admin/home" className="flex items-center gap-2 group">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-600">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
              />
            </svg>
          </div>
          <span className="text-sm font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
            Ocellus Admin
          </span>
        </a>

        {/* Right side */}
        <div className="flex items-center gap-2">
          {/* Manage dropdown */}
          {visibleManageLinks.length > 0 && (
            <div className="relative" ref={dropRef}>
              <button
                onClick={() => setOpen((v) => !v)}
                className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${
                  open
                    ? "border-blue-200 bg-blue-50 text-blue-700"
                    : "border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:text-gray-900"
                }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Add New
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-3.5 w-3.5 transition-transform duration-150 ${open ? "rotate-180" : ""}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {open && (
                <div className="absolute right-0 mt-1.5 w-44 rounded-xl border border-gray-100 bg-white py-1 shadow-lg shadow-gray-100/80">
                  {visibleManageLinks.map((link) => (
                    <a
                      key={link.href}
                      href={link.href}
                      onClick={() => setOpen(false)}
                      className="flex items-center gap-2.5 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-blue-400" />
                      {link.label}
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Logout */}
          <button
            onClick={logout}
            className="flex items-center gap-1.5 rounded-lg border border-red-100 bg-red-50 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-100 transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
            Logout
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
